import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { colors, TableBody, SearchActionBar } from '@insurancemenu/imenu-components-lib';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { Archive, Unarchive } from '@insurancemenu/imenu-components-lib/dist/icons';
import {
  useRFQProductTemplatesStore,
  WarningModalType,
} from 'store/pages/rfq/rfq-template/rfq-product-templates/pageStore';
import { ProductTemplateSortBy, ProductTemplateSortOrder } from 'types/productServiceV2';
import {
  ContentStyled,
  HeadingsRowStyled,
  TableHeaderStyled,
  TableHeadingStyled,
  TableWrapperStyled,
  TemplateTableStyled,
  TemplateNameStyled,
} from './styles';
import RFQProductTemplateRow from './RFQProductTemplateRow';
import TemplateInfoModal from '../modals/TemplateInfoModal';
import DuplicateProductTemplateModal from '../modals/DuplicateProductTemplateModal';
import {
  PRODUCT_TEMPLATES_TABLE_TEST_ID,
  PRODUCT_TEMPLATES_TABLE_HEADER_TEST_ID,
  PRODUCT_TEMPLATES_ARCHIVE_MODAL_TEST_ID,
  PRODUCT_TEMPLATES_UNARCHIVE_MODAL_TEST_ID,
} from '../constants';

const RFQProductTemplatesTable: React.FC = observer(() => {
  const {
    isLoading,
    productTemplates,
    isWarningModalOpen,
    currentProductTemplate,
    warningModalType,
    paginationDelegate: {
      filters: { state, status, searchProductName, productLineId, effectiveYear },
      page,
      totalPages,
      totalElements,
      sortBy,
      sortOrder,
      setFilterValue,
      setPage,
      setSortBy,
      setSortOrder,
      setTotalPages,
    },
    getInitialData,
    getProductTemplates,
    getProductLines,
    closeWarningModal,
    changeArchiveStatus,
  } = useRFQProductTemplatesStore();

  useEffect(() => {
    void getInitialData();
  }, []);

  useEffect(() => {
    void getProductTemplates();
  }, [page]);

  useEffect(() => {
    void getProductLines();
  }, [state, effectiveYear]);

  useEffect(() => {
    setPage(1);
    setTotalPages(1);
    void getProductTemplates();
  }, [productLineId, state, effectiveYear, status, sortBy, sortOrder, searchProductName]);

  // Actions
  const onWarningModalAction = (modalType: WarningModalType): void => {
    void changeArchiveStatus(modalType === 'ARCHIVE');
    closeWarningModal();
  };

  const onSearch = (value: string): void => {
    setFilterValue('searchProductName', value);
    setPage(1);
  };

  const onReset = (): void => {
    setFilterValue('searchProductName', '');
    setPage(1);
  };

  const onTableSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    setSortBy(sortKey as ProductTemplateSortBy);
    setSortOrder(sortingOrder as ProductTemplateSortOrder);
  };

  // Components
  const WarningModal = (): React.ReactNode => {
    const CurrentTemplateTitle = <TemplateNameStyled>{currentProductTemplate?.name || 'this'}</TemplateNameStyled>;

    switch (warningModalType) {
      case 'ARCHIVE':
        return (
          <ActionModal
            show={isWarningModalOpen}
            title="Archive Product Template"
            subtitle={<p>You are about to archive {CurrentTemplateTitle} template.</p>}
            actionButtonText="Archive Template"
            closeButtonText="Cancel"
            onClose={closeWarningModal}
            onAction={(): void => onWarningModalAction('ARCHIVE')}
            icon={<Archive width="5.6rem" color={colors.green[600]} />}
            data-test-id={PRODUCT_TEMPLATES_ARCHIVE_MODAL_TEST_ID}
          />
        );
      case 'UNARCHIVE':
        return (
          <ActionModal
            show={isWarningModalOpen}
            title="Unarchive Product Template"
            subtitle={<p>You are about to unarchive {CurrentTemplateTitle} template.</p>}
            actionButtonText="Unarchive Template"
            closeButtonText="Cancel"
            onClose={closeWarningModal}
            onAction={(): void => onWarningModalAction('UNARCHIVE')}
            icon={<Unarchive width="5.6rem" color={colors.green[600]} />}
            data-test-id={PRODUCT_TEMPLATES_UNARCHIVE_MODAL_TEST_ID}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ContentStyled>
      <TableWrapperStyled>
        <SearchActionBar
          totalElements={totalElements}
          totalTitle="Total Products"
          searchPlaceholder="Internal product name"
          isSearchButtonDisabled={isLoading}
          onSearch={onSearch}
          onReset={onReset}
        />
        <TemplateTableStyled
          loading={isLoading}
          maxHeight="55rem"
          minHeight="55rem"
          showScrollShadows
          showRowHover
          totalElements={totalElements}
          pagination={{
            onPageChange: setPage,
            maxPage: totalPages || 1,
            tablePage: page,
          }}
          data-test-id={PRODUCT_TEMPLATES_TABLE_TEST_ID}
        >
          <TableHeaderStyled data-test-id={PRODUCT_TEMPLATES_TABLE_HEADER_TEST_ID}>
            <HeadingsRowStyled>
              <TableHeadingStyled
                width="9rem"
                sorting
                sortingKey={ProductTemplateSortBy.NAME}
                onSortCallback={onTableSort}
              >
                Internal Product Name
              </TableHeadingStyled>
              <TableHeadingStyled width="8rem">External Product Name</TableHeadingStyled>
              <TableHeadingStyled width="5rem">Status</TableHeadingStyled>
              <TableHeadingStyled width="5rem">Product Line</TableHeadingStyled>
              <TableHeadingStyled width="6rem">Effective Date Range</TableHeadingStyled>
              <TableHeadingStyled
                width="6rem"
                sorting
                sortingKey={ProductTemplateSortBy.DATE}
                onSortCallback={onTableSort}
              >
                Creation Date
              </TableHeadingStyled>
              <TableHeadingStyled width="3rem">State</TableHeadingStyled>
              <TableHeadingStyled width="4rem" />
            </HeadingsRowStyled>
          </TableHeaderStyled>
          <TableBody>
            {productTemplates.map((product) => (
              <RFQProductTemplateRow key={product.id} productTemplate={product} />
            ))}
          </TableBody>
        </TemplateTableStyled>
        {/* Modals */}
        {WarningModal()}
        <TemplateInfoModal />
        <DuplicateProductTemplateModal />
      </TableWrapperStyled>
    </ContentStyled>
  );
});

export default RFQProductTemplatesTable;
